import type { ComponentType, SVGProps } from "react"
import { Phone01, MessageChatCircle, Tool01, CalendarCheck01, Flag01, Eye } from "@untitledui/icons"

import type { EstimateStatus } from "@/lib/api/types"
import { STATUS_CFG } from "@/components/estimates/status-badge"
import type {
  AnyTaskType,
  DeclineReason,
  EstimateFollowUpHead,
  FollowUpCadence,
  TaskAnswer,
  TaskDoc,
  TaskRow,
} from "@/lib/api/tasks"
import { whenLabel } from "@/lib/tasks/task-copy"

// The stage tells her what to do. Seven task types collapse into SIX kinds
// the advisor can tell apart at a glance — each with one chip label and one
// glyph, used identically in the list row and the card. The chips are
// monochrome on purpose: colour on this screen means STATE (overdue,
// interested, texts stopped) and the one action that wins (Book).

export type TaskKind = "review" | "call" | "reply" | "fix" | "visit" | "wrapup"
export type KindTab = "all" | "call" | "reply" | "other"

export interface KindSpec {
  kind: TaskKind
  label: string
  glyph: ComponentType<SVGProps<SVGSVGElement>>
  tokens: { chip: string; glyph: string; dot: string }
  /** The second line of a row / the card's sub-label. */
  subLabel: (row: TaskRow, cadence: FollowUpCadence | null) => string
  /** The context rail's sections, top to bottom, for what THIS stage needs:
   *  the calendar where a booking is the goal; the estimate to quote from;
   *  the customer (contact, VIN, where it came from) where reaching them is
   *  the question. "messages" only ever shows once the customer has written
   *  — nobody texts a customer who hasn't been contacted yet. */
  rail: ReadonlyArray<"calendar" | "messages" | "estimate" | "customer">
}

const first = (name: string) => name.split(" ")[0] || name

export const TASK_KIND: Record<TaskKind, KindSpec> = {
  review: {
    kind: "review",
    label: "Review",
    glyph: Eye,
    tokens: { chip: "bg-zinc-100 text-zinc-700 ring-zinc-200/70", glyph: "text-zinc-500", dot: "bg-zinc-400" },
    subLabel: () => "New estimate",
    // The estimate itself is the work on a review; beside it, who this is.
    rail: ["customer"],
  },
  call: {
    kind: "call",
    label: "Call",
    glyph: Phone01,
    tokens: { chip: "bg-zinc-100 text-zinc-700 ring-zinc-200/70", glyph: "text-zinc-500", dot: "bg-zinc-400" },
    subLabel: (r, cadence) => {
      if (r.type === "reach_out") return "First call"
      if (r.type === "callback") return `Call back · asked for ${whenLabel(r.dueAt, new Date(), false)}`
      if (r.isLastCall) return r.dripState === "finished" ? "Last call · texts finished" : "Last call"
      return cadence ? `Call ${r.attempt} of ${cadence.maxCalls}` : `Call ${r.attempt}`
    },
    rail: ["calendar", "messages", "estimate"],
  },
  reply: {
    kind: "reply",
    label: "Reply",
    glyph: MessageChatCircle,
    tokens: { chip: "bg-zinc-100 text-zinc-700 ring-zinc-200/70", glyph: "text-zinc-500", dot: "bg-zinc-400" },
    subLabel: () => "Waiting on your reply",
    rail: ["calendar", "estimate"],
  },
  fix: {
    kind: "fix",
    label: "Fix number",
    glyph: Tool01,
    tokens: { chip: "bg-zinc-100 text-zinc-700 ring-zinc-200/70", glyph: "text-zinc-500", dot: "bg-zinc-400" },
    subLabel: () => "",
    rail: ["customer", "messages"],
  },
  visit: {
    kind: "visit",
    label: "Visit",
    glyph: CalendarCheck01,
    tokens: { chip: "bg-zinc-100 text-zinc-700 ring-zinc-200/70", glyph: "text-zinc-500", dot: "bg-zinc-400" },
    subLabel: () => "",
    rail: ["calendar", "messages", "estimate"],
  },
  wrapup: {
    kind: "wrapup",
    label: "Wrap up",
    glyph: Flag01,
    tokens: { chip: "bg-zinc-900 text-white ring-zinc-900", glyph: "text-zinc-300", dot: "bg-zinc-900" },
    subLabel: (r) => `${r.head.attempts} calls, texts finished`,
    rail: ["calendar", "messages", "estimate"],
  },
}

const KIND_OF: Record<AnyTaskType, TaskKind> = {
  review: "review",
  reach_out: "call",
  call: "call",
  callback: "call",
  reply: "reply",
  fix_contact: "fix",
  showed: "visit",
  close_out: "wrapup",
}

export function kindOf(type: AnyTaskType): TaskKind {
  return KIND_OF[type]
}

const SAID: Record<NonNullable<TaskRow["lastSaid"]>["reason"], string> = {
  thinking: "said still thinking",
  needs_changes: "wants changes",
  callback: "asked for a call back",
  sent_estimate: "asked for the estimate",
}

/** What happened last on this estimate, as the result of a tap she made —
 *  "said still thinking Wed", "no answer Fri", "wrote back today". The list
 *  row's second line; nothing to interpret. */
export function lastHappened(row: TaskRow, now = new Date()): string | null {
  if (row.lastMessage?.direction === "inbound") return `wrote back ${whenLabel(row.lastMessage.occurredAt, now, false)}`
  if (row.lastSaid) return `${SAID[row.lastSaid.reason]} ${whenLabel(row.lastSaid.at, now, false)}`
  const h = row.head
  if (h.lastOutcome && h.lastAttemptAt && h.lastOutcome !== "reached") {
    const word = h.lastOutcome === "no_answer" ? "no answer" : h.lastOutcome === "voicemail" ? "left a voicemail" : "wrong number"
    return `${word} ${whenLabel(h.lastAttemptAt, now, false)}`
  }
  return null
}

export function tabOf(kind: TaskKind): KindTab {
  return kind === "call" || kind === "reply" ? kind : "other"
}

export function matchesKindTab(row: TaskRow, tab: KindTab): boolean {
  return tab === "all" || tabOf(kindOf(row.type)) === tab
}

export const KIND_TABS: ReadonlyArray<{ key: KindTab; label: string }> = [
  { key: "all", label: "All" },
  { key: "call", label: "Calls" },
  { key: "reply", label: "Replies" },
  { key: "other", label: "Other" },
]

// ── The stages ───────────────────────────────────────────────────────────
// The pipeline's own words and colours (STATUS_CFG) — never a second
// vocabulary. Declined is an outcome, not a stop on the strip.

export const STAGES: ReadonlyArray<EstimateStatus> = ["Pending", "Confirmed", "InProgress", "Scheduled", "Completed"]

export function stageLabel(status: string): string {
  return STATUS_CFG[status as EstimateStatus]?.label ?? status
}

/** Index on the strip; Loading sits with Pending, Draft before it. */
export function stageIndex(status: string): number {
  if (status === "Loading") return 0
  if (status === "Draft") return -1
  return STAGES.indexOf(status as EstimateStatus)
}

/** What the panel reports back — the ONE thing the page toasts. */
export type TaskResult =
  | {
      kind: "answered"
      answer: TaskAnswer
      next: TaskDoc | null
      bookedAt?: string
      /** The head as the server rebuilt it after this answer. */
      head?: EstimateFollowUpHead
      /** Confirmed AND moved to In Progress in one go — the texts start today. */
      startedTexts?: boolean
    }
  /** A booking made from a task that has no id to answer (a reply). */
  | { kind: "booked"; at: string }
  | { kind: "visit"; outcome: "attended" | "no_show"; rebookedAt?: string }
  /** Back to In Progress with a call in N business days (after an inspection). */
  | { kind: "follow_up"; inDays: number }
  /** Declined from somewhere that isn't a task answer (a reply). */
  | { kind: "declined"; reason: DeclineReason }
  | { kind: "replied" }
  | { kind: "texts_stopped" }
  | { kind: "skipped"; dueAt: string }
  | { kind: "snoozed"; until: string }

/** Where the answer just moved them — lit on the strip before the card advances. */
export function stageAfter(row: TaskRow, result: TaskResult): EstimateStatus {
  const now = row.estimateStatus as EstimateStatus
  switch (result.kind) {
    case "answered": {
      const a = result.answer
      if (a.kind === "confirmed") return result.startedTexts ? "InProgress" : "Confirmed"
      if (a.kind === "reached" && a.then === "booked") return "Scheduled"
      if ((a.kind === "reached" && a.then === "declined") || a.kind === "closed") return "Declined"
      if (now === "Confirmed" && (a.kind === "reached" || a.kind === "no_answer" || a.kind === "voicemail")) return "InProgress"
      return now
    }
    case "booked":
      return "Scheduled"
    case "visit":
      if (result.rebookedAt) return "Scheduled"
      if (result.outcome === "attended") return "Completed"
      return "InProgress"
    case "follow_up":
      return "InProgress"
    case "declined":
      return "Declined"
    default:
      return now
  }
}

/** The next task the engine handed back that belongs on THIS card — the
 *  same customer, due now — so the screen continues in place instead of
 *  jumping to someone else (wrong number → fix it; last call → wrap up;
 *  review → the first call). */
export function continuesInPlace(row: TaskRow, result: TaskResult, now = Date.now()): TaskDoc | null {
  if (result.kind !== "answered" || !result.next) return null
  const n = result.next
  if (n.estimateId !== row.estimateId) return null
  return new Date(n.dueAt).getTime() <= now + 60_000 ? n : null
}

// ── The facts line ───────────────────────────────────────────────────────
// Short labelled fragments under the name, never prose: "Last call: voicemail
// Fri · Texts: 2 of 5 sent". Only what exists, and nothing the chip or the
// stage already says. The story lives in History, folded.

function lastCall(row: TaskRow, meId: number | null, now: Date): string | null {
  const h = row.head
  if (!h.lastAttemptAt || !h.lastOutcome) return null
  const when = whenLabel(h.lastAttemptAt, now, false)
  const by = h.lastUserId != null && h.lastUserId === meId ? null : h.lastUserName ? h.lastUserName.split(" ")[0] : null
  const said = row.lastSaid ? SAID[row.lastSaid.reason] : null
  const what =
    h.lastOutcome === "reached" ? (said ?? "reached them") : h.lastOutcome === "voicemail" ? "voicemail" : h.lastOutcome === "no_answer" ? "no answer" : "wrong number"
  return `${what} ${when}${by ? ` (${by})` : ""}`
}

function textsShort(row: TaskRow, now: Date): { text: string; off: boolean } | null {
  const s = row.estimateStatus
  // Not started yet: the stage strip says so; nothing to add.
  if (s === "Pending" || s === "Confirmed" || s === "Draft" || s === "Loading") return null
  switch (row.dripState) {
    case "active":
      return { text: `${row.textsSent} of ${row.textsTotal} sent${row.nextTouchAt ? `, next ${whenLabel(row.nextTouchAt, now, false)}` : ""}`, off: false }
    case "paused":
      return { text: `paused until ${whenLabel(row.dripResumesAt ?? row.dueAt, now, false)}`, off: false }
    case "held":
      return { text: row.heldBy === "reply" ? "paused until you reply" : "paused", off: false }
    case "finished":
      return { text: `finished, ${row.textsSent} sent`, off: false }
    case "stopped": {
      const st = row.textsStopped
      if (st?.by === "customer") return { text: "stopped, they texted STOP", off: true }
      if (st?.by === "user") return { text: `stopped by ${st.name?.split(" ")[0] ?? "someone"}`, off: true }
      return { text: "off", off: true }
    }
    case "converted":
      return { text: "stopped, booked", off: false }
    case "not_started":
    default:
      return { text: "none, no number or email", off: true }
  }
}

export interface Facts {
  /** "voicemail Fri (Tom)" — the last dial, dated. */
  lastCall: string | null
  /** "wrote back today" — the customer's last message, if it was theirs. */
  wroteBack: string | null
  texts: { text: string; off: boolean } | null
}

export function facts(row: TaskRow, meId: number | null, now = new Date()): Facts {
  return {
    lastCall: lastCall(row, meId, now),
    wroteBack: row.lastMessage?.direction === "inbound" ? `wrote back ${whenLabel(row.lastMessage.occurredAt, now, false)}` : null,
    texts: textsShort(row, now),
  }
}
